"use client";
import { useGlobalContent } from "@app/[locale]/GlobalContentProvider";
import {
    containerStyles,
    linksContainerStyles,
    logoStyles,
    mobileLogoContainerStyles,
    navContainerStyles,
} from "@components/layouts/navigation/styles";
import { NextLink } from "@components/ui/next-link/NextLink";
import type { NavigationModel } from "@lib/contentful/mappers/mapNavigation";
import {
    DESKTOP_OPEN_SEARCH_ICON,
    MAIN_MENU,
    MOBILE_OPEN_SEARCH_ICON,
    NAVIGATION,
} from "@e2e/helpers/navigation/constants";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { useRouteChange } from "@store/hooks/useRouteChange";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { VisuallyHidden } from "@ecco/ui/visually-hidden";
import { useXlBreakpoint } from "@ecco/ui/use-breakpoint";
import dynamic from "next/dynamic";
import type { FC } from "react";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { sizes, space } from "@ecco/ui/tokens";
import { Logo } from "./components/Logo";
import RightNavigation from "./components/RightNavigation";
import { DesktopNav } from "./desktop/DesktopNav";
import { MobileNavigationContainer } from "./mobile/MobileNavigationContainer";
import { Container } from "@ecco/ui/container";
import { IconButton } from "@ecco/ui/icon-button";
import { ECCOIcon } from "@ecco/ui/ecco-icon";
import { FullBleed } from "@ecco/ui/full-bleed";

export interface NavigationProps {
    navigation: NavigationModel;
}

const SearchBarLazy = dynamic(
    () => import("@components/layouts/navigation/search/SearchBar").then((mod) => mod.SearchBar),
    { ssr: true }
);

/** Extra scroll range before hide-on-scroll kicks in, beyond content above the nav bar. */
const NAV_HIDE_SCROLL_BASE_PX = 50;
/** Only sample “pixels above nav” while this close to the document top (nav may be sticky below). */
const NAV_ABOVE_SAMPLE_MAX_SCROLL_Y = 8;

export const Navigation: FC<NavigationProps> = memo(({ navigation }) => {
    const { get: getMicrocopy } = useMicrocopy();
    const { accessibilityStatementPath } = useGlobalContent();

    const [searchBarOpen, setSearchBarOpen] = useState(false);
    const [isNavOpen, setIsNavOpen] = useState(false);
    const [hasHydrated, setHasHydrated] = useState(false);
    const [showNav, setShowNav] = useState(true);
    const lastScrollY = useRef(0);
    const navRef = useRef<HTMLElement>(null);
    const aboveNavScrollBufferRef = useRef(0);

    const clientIsDesktop = useXlBreakpoint();

    // Overlay shown for both nav dropdown and search
    const showOverlay = isNavOpen || searchBarOpen;

    useEffect(() => {
        setHasHydrated(true);
    }, []);

    // Keep --nav-dropdown-top in sync with the nav bar's viewport bottom edge
    // so the Portal'd dropdown always renders right below the bar, even when
    // third-party banners (e.g. Yieldify) are injected above it.
    useEffect(() => {
        const navEl = navRef.current;
        if (!navEl) return;

        let lastBottom = -1;
        const sync = () => {
            const rect = navEl.getBoundingClientRect();
            const bottom = Math.round(Math.max(0, rect.bottom));
            if (bottom !== lastBottom) {
                lastBottom = bottom;
                document.documentElement.style.setProperty("--nav-dropdown-top", `${bottom}px`);
            }
            if (window.scrollY <= NAV_ABOVE_SAMPLE_MAX_SCROLL_Y) {
                aboveNavScrollBufferRef.current = Math.round(Math.max(0, rect.top));
            }
        };

        sync();
        window.addEventListener("scroll", sync, { passive: true });
        window.addEventListener("resize", sync);

        const mo = new MutationObserver(() => requestAnimationFrame(sync));
        mo.observe(document.body, { childList: true });

        return () => {
            window.removeEventListener("scroll", sync);
            window.removeEventListener("resize", sync);
            mo.disconnect();
            document.documentElement.style.removeProperty("--nav-dropdown-top");
        };
    }, []);

    // Hide nav on scroll down, show on scroll up — disabled while dropdown is open
    useEffect(() => {
        const handleScroll = () => {
            if (isNavOpen) return;
            const currentScrollY = window.scrollY;
            if (Math.abs(currentScrollY - lastScrollY.current) < 5) return;
            const keepVisibleUntil = NAV_HIDE_SCROLL_BASE_PX + aboveNavScrollBufferRef.current;
            setShowNav(currentScrollY <= lastScrollY.current || currentScrollY <= keepVisibleUntil);
            lastScrollY.current = currentScrollY;
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, [isNavOpen]);

    // No scroll lock here — scroll is blocked only when mouse hovers the dropdown
    // (handled in DesktopNavItem) to avoid Windows scrollbar flicker on open/close.

    useRouteChange(() => {
        setSearchBarOpen(false);
        setIsNavOpen(false);
    });

    const closeSearchBarAndRestoreFocus = useCallback(() => {
        if (!searchBarOpen) return;
        setSearchBarOpen(false);
        const testId = clientIsDesktop ? DESKTOP_OPEN_SEARCH_ICON : MOBILE_OPEN_SEARCH_ICON;
        const triggerButton = document.querySelector<HTMLElement>(`[data-testid="${testId}"]`);
        triggerButton?.focus();
    }, [searchBarOpen, clientIsDesktop]);

    const skipLinkText =
        getMicrocopy(CommonCMS.shared, CommonCMS.ariaLabels.skipLinkTextAria) ||
        "Skip to main Content";

    const searchButtonAriaLabel =
        getMicrocopy(CommonCMS.shared, CommonCMS.searchBarAriaIntro) || "Search";

    return (
        <>
            <VisuallyHidden>
                <a id="primarySkipLink" href="#skipLinkNavPages">
                    {skipLinkText}
                </a>
            </VisuallyHidden>

            {/* Dim overlay — always mounted, fades in/out to avoid flicker on item switch */}
            <Box
                position="fixed"
                top={0}
                left={0}
                width="100%"
                height="100%"
                bg="blackAlpha.600"
                zIndex={9}
                opacity={showOverlay ? 1 : 0}
                visibility={showOverlay ? "visible" : "hidden"}
                transition="opacity 0.15s ease, visibility 0.15s ease"
                pointerEvents={showOverlay ? "auto" : "none"}
                onClick={() => {
                    setIsNavOpen(false);
                    setSearchBarOpen(false);
                }}
            />

            <FullBleed
                position="sticky"
                top={0}
                zIndex={11}
                bg="white"
                transition="transform 0.3s ease-in-out"
                style={{
                    transform: showNav ? "translateY(0)" : "translateY(-100%)",
                }}
            >
                <Container
                    ref={navRef}
                    {...navContainerStyles}
                    as="nav"
                    aria-label={
                        getMicrocopy(CommonCMS.shared, CommonCMS.ariaLabels.navigationAria) ||
                        "Main navigation"
                    }
                    id="navigation"
                    data-testid={MAIN_MENU}
                >
                    <Box data-testid={NAVIGATION} {...containerStyles}>
                        {accessibilityStatementPath && (
                            <VisuallyHidden>
                                <NextLink href={accessibilityStatementPath} target="_self">
                                    {getMicrocopy(
                                        CommonCMS.shared,
                                        CommonCMS.accessibilityStatement
                                    ) || "Accessibility Statement"}
                                </NextLink>
                            </VisuallyHidden>
                        )}

                        {/* Mobile Navigation */}
                        {!clientIsDesktop && (
                            <Flex
                                display={{ base: "flex", xl: "none" }}
                                align="center"
                                justify="flex-start"
                                w="100%"
                            >
                                <MobileNavigationContainer
                                    navigation={navigation}
                                    searchBarOpen={searchBarOpen}
                                />

                                <Flex {...mobileLogoContainerStyles}>
                                    <Logo />
                                </Flex>

                                <Flex align="center" ml="auto">
                                    <Flex
                                        width="auto"
                                        height={{ base: "100%", sm: "100%" }}
                                        top={0}
                                        left={0}
                                        gap={space["0.2"]}
                                        justifyContent="flex-end"
                                    >
                                        <IconButton
                                            data-testid={MOBILE_OPEN_SEARCH_ICON}
                                            variant="tertiary"
                                            margin="0"
                                            h={sizes.navBarCollapsedHeight}
                                            w="9" // Accessibility requires a minimum touch target of 44x44px
                                            bg="white"
                                            alignItems="center"
                                            justifyContent="center"
                                            aria-label={searchButtonAriaLabel}
                                            onClick={() => setSearchBarOpen(true)}
                                            icon={<ECCOIcon name="search" />}
                                            aria-disabled={searchBarOpen}
                                            tabIndex={searchBarOpen ? -1 : undefined}
                                        />
                                    </Flex>
                                    <RightNavigation />
                                </Flex>
                            </Flex>
                        )}

                        {/* Desktop Navigation */}
                        {(hasHydrated ? clientIsDesktop : true) && (
                            <Flex
                                display={{ base: "none", xl: "flex" }}
                                w="100%"
                                h="100%"
                                align="stretch"
                                justify="space-between"
                            >
                                {/* Left: Logo + Categories */}
                                <Flex
                                    align="stretch"
                                    gap={space["0.2"]}
                                    flexShrink={1}
                                    minW={0}
                                    overflow="hidden"
                                >
                                    <Flex {...logoStyles} alignSelf="center">
                                        <Logo />
                                    </Flex>
                                    <Flex sx={linksContainerStyles} w="auto" h="100%">
                                        <DesktopNav
                                            navigation={navigation}
                                            searchBarOpen={searchBarOpen}
                                            onCloseSearchBar={() => setSearchBarOpen(false)}
                                            onOpenSearchBar={() => setSearchBarOpen(true)}
                                            setIsNavOpen={setIsNavOpen}
                                        />
                                    </Flex>
                                </Flex>

                                {/* Right: Search + Account/Cart */}
                                <Flex align="center" alignSelf="center" flexShrink={0}>
                                    <Flex
                                        width="auto"
                                        alignItems="center"
                                        top={0}
                                        left={0}
                                        gap={space["0.2"]}
                                        justifyContent="flex-end"
                                    >
                                        <IconButton
                                            data-testid={DESKTOP_OPEN_SEARCH_ICON}
                                            variant="tertiary"
                                            h={sizes.navBarCollapsedHeight}
                                            w={sizes.navBarCollapsedHeight}
                                            bg="white"
                                            alignItems="center"
                                            justifyContent="center"
                                            aria-label={searchButtonAriaLabel}
                                            onClick={() => setSearchBarOpen(true)}
                                            icon={<ECCOIcon name="search" />}
                                            aria-disabled={searchBarOpen}
                                            tabIndex={searchBarOpen ? -1 : undefined}
                                        />
                                    </Flex>
                                    <RightNavigation />
                                </Flex>
                            </Flex>
                        )}

                        <SearchBarLazy
                            searchBarOpen={searchBarOpen}
                            onClose={closeSearchBarAndRestoreFocus}
                        />
                    </Box>
                </Container>
            </FullBleed>

            <div aria-hidden="true" id="skipLinkNavPages" />
        </>
    );
});

Navigation.displayName = "Navigation";
